import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, Plus, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  approvePayroll, downloadAccountsFile, getPayroll, getPayrollOverview, importSalaries, listSalaries, payrollPaid,
  previewSalaryImport, reopenPayroll, savePayroll,
} from "@/lib/api";
import { Empty, Stat, TH, TR, errMsg, money } from "@/components/accounts/shared";
import { localeFor } from "@/lib/money";
import { COUNTRIES, DEFAULTS, countryFor, fromCtc, fromInHand } from "@/lib/ctc";

// Salaries: accounts only. Monthly payroll sheet per gym, and base salaries (imported).
export default function Salaries({ month, gym, gyms }) {
  const [view, setView] = useState("payroll");
  return (
    <div className="space-y-4" data-testid="accounts-salaries">
      <div className="flex gap-1.5">
        {[["payroll", "Payroll"], ["base", "Base salaries"], ["ctc", "CTC calculator"]].map(([k, l]) => (
          <button key={k} onClick={() => setView(k)} className={`text-xs px-3 py-1 rounded-full border ${view === k ? "border-blue-500/60 bg-blue-500/10 text-blue-200" : "border-[#262626] text-zinc-400"}`}>{l}</button>
        ))}
        <span className="text-[11px] text-zinc-500 self-center ml-2">Visible only to admins and the accounts team.</span>
      </div>
      {view === "payroll" ? <Payroll month={month} gym={gym} /> : view === "base" ? <BaseSalaries gym={gym} gyms={gyms} />
        : <CtcCalculator gym={gym} gyms={gyms} />}
    </div>
  );
}

function Payroll({ month, gym }) {
  const [overview, setOverview] = useState(null);
  const [pick, setPick] = useState(gym || "");
  useEffect(() => { setPick(gym || ""); }, [gym]);
  const load = useCallback(() => getPayrollOverview(month).then(setOverview).catch((e) => toast.error(errMsg(e, "Could not load payroll"))), [month]);
  useEffect(() => { load(); }, [load]);
  if (!overview) return <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>;
  return (
    <div className="space-y-4">
      <div className="card-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className={TH}><th className="px-3 py-2 font-medium">Gym</th><th className="px-3 py-2 font-medium">Status</th>
            <th className="px-3 py-2 font-medium text-right">People</th><th className="px-3 py-2 font-medium text-right">Net pay</th></tr></thead>
          <tbody>
            {overview.map((g) => (
              <tr key={g.facility_id} onClick={() => setPick(g.facility_id)} className={`${TR} cursor-pointer ${pick === g.facility_id ? "bg-[#161616]" : ""}`}>
                <td className="px-3 py-2 text-zinc-200">{g.gym}</td>
                <td className={`px-3 py-2 text-xs ${g.status === "paid" ? "text-emerald-300" : g.status === "approved" ? "text-blue-300" : g.status === "draft" ? "text-amber-300" : "text-zinc-500"}`}>{g.status}</td>
                <td className="px-3 py-2 text-right tabular-nums text-zinc-400">{g.totals?.people ?? "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums">{g.totals ? money(g, g.totals.net) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pick && <Sheet key={`${pick}:${month}`} facilityId={pick} month={month} onChanged={load} />}
    </div>
  );
}

function Sheet({ facilityId, month, onChanged }) {
  const [s, setS] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [paying, setPaying] = useState(false);
  const load = useCallback(() => getPayroll(facilityId, month).then((d) => { setS(d); setDirty(false); }).catch((e) => toast.error(errMsg(e, "Could not load"))), [facilityId, month]);
  useEffect(() => { load(); }, [load]);
  if (!s) return <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>;
  const draft = s.status === "draft";
  const net = (ln) => ln.base + ln.incentives.reduce((a, i) => a + i.amount, 0)
    + ln.adjustments.reduce((a, x) => a + (x.kind === "addition" ? 1 : -1) * (Number(x.amount) || 0), 0);
  const edit = (uid, fn) => { setS({ ...s, lines: s.lines.map((ln) => (ln.user_id === uid ? fn(ln) : ln)) }); setDirty(true); };
  const save = async () => {
    try {
      const out = await savePayroll({ facility_id: facilityId, month, lines: s.lines.map((ln) => ({ user_id: ln.user_id, note: ln.note || "",
        adjustments: ln.adjustments.map((a) => ({ ...a, amount: Number(a.amount) || 0 })) })) });
      setS(out); setDirty(false); toast.success("Draft saved"); onChanged();
    } catch (e) { toast.error(errMsg(e, "Could not save")); }
  };
  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); onChanged(); } catch (e) { toast.error(errMsg(e, "Could not save")); }
  };
  const total = s.lines.reduce((a, ln) => a + net(ln), 0);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-base font-semibold text-zinc-100">{s.gym} · {month} <span className="text-xs font-normal text-zinc-500 ml-1">{s.status}{s.saved === false ? " (not saved yet)" : ""}</span></div>
        <div className="flex flex-wrap gap-2">
          {draft && <Button size="sm" onClick={save} className="bg-blue-600 hover:bg-blue-500 h-8" data-testid="payroll-save">Save draft</Button>}
          {draft && s.saved && !dirty && <Button size="sm" variant="outline" onClick={() => act(() => approvePayroll(facilityId, month), "Approved")} className="h-8 border-[#262626] bg-transparent" data-testid="payroll-approve">Approve</Button>}
          {s.status === "approved" && <>
            <Button size="sm" variant="ghost" onClick={() => act(() => reopenPayroll(facilityId, month), "Back to draft")} className="h-8">Reopen</Button>
            <Button size="sm" variant="outline" onClick={() => downloadAccountsFile("/accounts/payroll/bank-file", { facility_id: facilityId, month }, `salaries-${month}.csv`)}
              className="h-8 border-[#262626] bg-transparent"><Download className="h-3.5 w-3.5 mr-1" /> Bank upload CSV</Button>
            <Button size="sm" onClick={() => setPaying(true)} className="bg-blue-600 hover:bg-blue-500 h-8">Mark paid</Button>
          </>}
          {s.status === "paid" && <span className="text-xs text-emerald-300">Paid {s.paid_date} · {s.bank_ref}</span>}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat label="People" value={s.lines.length} />
        <Stat label="Base" value={money(s, s.lines.reduce((a, ln) => a + ln.base, 0))} />
        <Stat label="Incentives" value={money(s, s.lines.reduce((a, ln) => a + ln.incentives.reduce((b, i) => b + i.amount, 0), 0))} sub="from the gym's Incentives" />
        <Stat label="Net pay" value={money(s, total)} />
      </div>
      {!s.leave_module && <div className="text-[11px] text-zinc-500">Leave isn't recorded on this server yet, so the sheet shows clock-in only.</div>}
      {s.unassigned_incentives?.length > 0 && (
        <div className="text-[11px] text-amber-300">Incentives with no matching person on this gym's team: {s.unassigned_incentives.map((i) => `${i.name} (${i.source}, ${money(s, i.amount)})`).join(", ")}</div>
      )}
      <div className="card-surface overflow-x-auto">
        {s.lines.length === 0 ? <Empty>No staff with a salary, shifts or incentives at this gym. Import base salaries first.</Empty> : (
          <table className="w-full text-sm min-w-[980px]">
            <thead><tr className={TH}><th className="px-3 py-2 font-medium">Person</th><th className="px-3 py-2 font-medium text-right">Base</th>
              <th className="px-3 py-2 font-medium">Clock-in</th><th className="px-3 py-2 font-medium">Incentives</th>
              <th className="px-3 py-2 font-medium">Additions / deductions / advances</th><th className="px-3 py-2 font-medium text-right">Net</th></tr></thead>
            <tbody>
              {s.lines.map((ln) => (
                <tr key={ln.user_id} className={`${TR} align-top`}>
                  <td className="px-3 py-2"><div className="text-zinc-200">{ln.name}</div><div className="text-[10px] text-zinc-500">{ln.email}</div>
                    {ln.warning && <div className="text-[10px] text-amber-300">{ln.warning}</div>}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(s, ln.base)}{ln.base_effective_from && <div className="text-[10px] text-zinc-500">from {ln.base_effective_from}</div>}</td>
                  <td className="px-3 py-2 text-xs text-zinc-400">{ln.attendance.days_worked} days · {ln.attendance.hours} h
                    <div className="text-[10px] text-zinc-500">{ln.attendance.shifts} shifts · {ln.attendance.missed} missed · {ln.attendance.late} late</div>
                    {ln.leave && <div className="text-[10px] text-zinc-500">leave {ln.leave.days} d</div>}</td>
                  <td className="px-3 py-2 text-xs text-zinc-400">{ln.incentives.length ? ln.incentives.map((i) => <div key={i.source}>{i.source}: {money(s, i.amount)}</div>) : "—"}</td>
                  <td className="px-3 py-2 text-xs">
                    {ln.adjustments.map((a, i) => (
                      <div key={i} className="flex items-center gap-1 mb-1">
                        <Input disabled={!draft} value={a.label} onChange={(e) => edit(ln.user_id, (l) => ({ ...l, adjustments: l.adjustments.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) }))} className="h-7 bg-[#0a0a0a] border-[#262626] w-36" />
                        <select disabled={!draft} value={a.kind} onChange={(e) => edit(ln.user_id, (l) => ({ ...l, adjustments: l.adjustments.map((x, j) => (j === i ? { ...x, kind: e.target.value } : x)) }))} className="h-7 bg-[#0a0a0a] border border-[#262626] rounded px-1">
                          <option value="addition">+ addition</option><option value="deduction">− deduction</option><option value="advance">− advance</option>
                        </select>
                        <Input disabled={!draft} type="number" value={a.amount} onChange={(e) => edit(ln.user_id, (l) => ({ ...l, adjustments: l.adjustments.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)) }))} className="h-7 bg-[#0a0a0a] border-[#262626] w-24" />
                        {draft && <button onClick={() => edit(ln.user_id, (l) => ({ ...l, adjustments: l.adjustments.filter((_, j) => j !== i) }))} className="text-zinc-500 hover:text-rose-300" aria-label="Remove line"><Trash2 className="h-3.5 w-3.5" /></button>}
                      </div>
                    ))}
                    {draft && <button onClick={() => edit(ln.user_id, (l) => ({ ...l, adjustments: [...l.adjustments, { label: "", kind: "deduction", amount: 0 }] }))} className="text-blue-300 text-[11px] inline-flex items-center gap-0.5"><Plus className="h-3 w-3" /> line</button>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-zinc-100">{money(s, net(ln))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {paying && <PaidDialog onClose={() => setPaying(false)} onSave={(p) => act(() => payrollPaid({ facility_id: facilityId, month, ...p }), "Marked paid").then(() => setPaying(false))} />}
    </div>
  );
}

function PaidDialog({ onClose, onSave }) {
  const [ref, setRef] = useState("");
  const [day, setDay] = useState(new Date().toISOString().slice(0, 10));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-sm">
        <DialogHeader><DialogTitle>Salaries paid</DialogTitle></DialogHeader>
        <Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Bank batch reference" className="bg-[#0a0a0a] border-[#262626]" />
        <Input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="bg-[#0a0a0a] border-[#262626]" />
        <p className="text-[11px] text-zinc-500">Each person's net pay then shows as a debit in bank reconciliation.</p>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button disabled={!ref.trim()} onClick={() => onSave({ bank_ref: ref, paid_date: day })} className="bg-blue-600 hover:bg-blue-500">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BaseSalaries({ gym }) {
  const [rows, setRows] = useState(null);
  const [prev, setPrev] = useState(null);           // {file, matched, unmatched}
  const fileRef = useRef(null);
  const load = useCallback(() => listSalaries(gym).then(setRows).catch((e) => toast.error(errMsg(e, "Could not load"))), [gym]);
  useEffect(() => { load(); }, [load]);
  const onFile = async (file) => {
    if (!file) return;
    try { setPrev({ file, ...(await previewSalaryImport(file)) }); } catch (e) { toast.error(errMsg(e, "Could not read the file")); }
    if (fileRef.current) fileRef.current.value = "";
  };
  const confirm = async () => {
    try { const r = await importSalaries(prev.file); toast.success(`${r.saved} salaries saved`); setPrev(null); load(); }
    catch (e) { toast.error(errMsg(e, "Import failed")); }
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={() => downloadAccountsFile("/accounts/salaries/template", {}, "salary-import-template.csv")} className="h-8 border-[#262626] bg-transparent">
          <Download className="h-3.5 w-3.5 mr-1" /> Template</Button>
        <input ref={fileRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
        <Button size="sm" onClick={() => fileRef.current?.click()} className="bg-blue-600 hover:bg-blue-500 h-8" data-testid="salary-import"><Upload className="h-3.5 w-3.5 mr-1" /> Import salaries</Button>
        <span className="text-[11px] text-zinc-500 self-center">Columns: email (or name), gym, monthly base salary, currency, effective from. History is kept by effective date.</span>
      </div>
      <div className="card-surface overflow-x-auto">
        {!rows ? <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>
          : rows.length === 0 ? <Empty>No base salaries yet. Download the template, fill it in and import it.</Empty> : (
            <table className="w-full text-sm">
              <thead><tr className={TH}><th className="px-3 py-2 font-medium">Person</th><th className="px-3 py-2 font-medium">Gym</th>
                <th className="px-3 py-2 font-medium text-right">Current base</th><th className="px-3 py-2 font-medium">History</th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.user_id}:${r.facility_id}`} className={TR}>
                    <td className="px-3 py-2 text-zinc-200">{r.name}<div className="text-[10px] text-zinc-500">{r.email}</div></td>
                    <td className="px-3 py-2 text-zinc-400">{r.gym}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.current ? money(r, r.current.amount) : "—"}{r.current && <div className="text-[10px] text-zinc-500">from {r.current.effective_from}</div>}</td>
                    <td className="px-3 py-2 text-[11px] text-zinc-500">{r.history.map((h) => `${h.effective_from}: ${money(r, h.amount)}`).join(" · ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>
      {prev && (
        <Dialog open onOpenChange={(o) => !o && setPrev(null)}>
          <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-2xl">
            <DialogHeader><DialogTitle>Check the import</DialogTitle></DialogHeader>
            <div className="text-sm text-zinc-300">{prev.matched.length} matched to FPA users · <span className={prev.unmatched.length ? "text-amber-300" : ""}>{prev.unmatched.length} not matched (skipped)</span></div>
            <div className="max-h-72 overflow-y-auto text-xs">
              {prev.matched.map((m) => <div key={m.row} className="py-0.5 text-zinc-400">Row {m.row}: {m.user_name} · {m.gym_name} · {m.currency_code} {m.amount.toLocaleString(localeFor(m.currency_code))} from {m.effective_from}{m.warning ? <span className="text-amber-300"> ({m.warning})</span> : ""}</div>)}
              {prev.unmatched.map((m) => <div key={m.row} className="py-0.5 text-amber-300">Row {m.row}: {m.email || m.name} · {m.gym}: {m.problems.join("; ")}</div>)}
            </div>
            <DialogFooter><Button variant="ghost" onClick={() => setPrev(null)}>Cancel</Button>
              <Button disabled={!prev.matched.length} onClick={confirm} className="bg-blue-600 hover:bg-blue-500" data-testid="salary-confirm">Save {prev.matched.length}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}


// CTC calculator: from the monthly in-hand someone expects to the annual package we offer, or back. Rules in lib/ctc.js.
function CtcCalculator({ gym, gyms }) {
  const start = countryFor((gyms || []).find((g) => g.id === gym)?.currency_code);
  const [country, setCountry] = useState(start);
  const [mode, setMode] = useState("inhand");           // inhand: in-hand a month -> CTC | ctc: annual CTC -> in-hand
  const [amount, setAmount] = useState("");
  const [opt, setOpt] = useState(DEFAULTS[start]);
  useEffect(() => { setOpt(DEFAULTS[country]); }, [country]);
  const cur = { currency_code: COUNTRIES[country].currency, currency_symbol: { IN: "₹", LK: "LKR ", AE: "AED " }[country] };
  const m = (n) => money(cur, n);
  const value = Number(amount) || 0;
  const r = value > 0 ? (mode === "inhand" ? fromInHand(country, value, opt) : fromCtc(country, value, opt)) : null;
  const set = (k, v) => setOpt((o) => ({ ...o, [k]: v }));
  const num = (k, label) => (
    <label className="text-xs text-zinc-400 space-y-1">
      <span>{label}</span>
      <Input type="number" min="0" value={opt[k] || ""} onChange={(e) => set(k, Number(e.target.value) || 0)} className="h-8" />
    </label>
  );
  const copy = () => {
    const lines = [
      `Annual package (CTC): ${m(r.ctc)}`, "",
      "Monthly", `Basic: ${m(r.basic)}`, `Allowances: ${m(r.allowances)}`, `Gross: ${m(r.gross)}`,
      ...r.employeeDeductions.map(([l, v]) => `Less ${l}: ${m(v)}`), `Estimated in-hand: ${m(r.inHand)}`, "",
      "Paid by LIFTR on top of gross",
      ...r.employerContributions.map(([l, v]) => `${l}: ${m(v)} a month`),
      ...r.annualExtras.map(([l, v]) => `${l}: ${m(v)} a year`),
    ];
    navigator.clipboard.writeText(lines.join("\n")).then(() => toast.success("Breakdown copied"), () => toast.error("Couldn't copy"));
  };
  return (
    <div className="space-y-4" data-testid="ctc-calculator">
      <div className="card-surface p-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex gap-1.5">
            {Object.entries(COUNTRIES).map(([k, c]) => (
              <button key={k} onClick={() => setCountry(k)} className={`text-xs px-3 py-1.5 rounded-full border ${country === k ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-200" : "border-[#262626] text-zinc-400"}`}>{c.label}</button>
            ))}
          </div>
          <div className="flex gap-1.5">
            {[["inhand", "From in-hand a month"], ["ctc", "From annual CTC"]].map(([k, l]) => (
              <button key={k} onClick={() => setMode(k)} className={`text-xs px-3 py-1.5 rounded-full border ${mode === k ? "border-blue-500/60 bg-blue-500/10 text-blue-200" : "border-[#262626] text-zinc-400"}`}>{l}</button>
            ))}
          </div>
          <label className="text-xs text-zinc-400 space-y-1">
            <span>{mode === "inhand" ? `In-hand a month (${cur.currency_code})` : `Annual CTC (${cur.currency_code})`}</span>
            <Input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-9 w-44 font-mono" data-testid="ctc-amount" autoFocus />
          </label>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {num("basicPct", "Basic, % of gross")}
          {num("insuranceYear", "Health insurance a year")}
          {country === "AE" && num("visaYear", "Visa + labour card a year")}
          {num("bonusYear", "Annual bonus / variable")}
          {country === "IN" && (
            <label className="text-xs text-zinc-400 space-y-1">
              <span>Professional tax (state)</span>
              <select value={opt.state} onChange={(e) => set("state", e.target.value)} className="h-8 w-full bg-[#0a0a0a] border border-[#262626] rounded-md px-2 text-sm text-zinc-100">
                <option value="KA">Karnataka (Bangalore)</option><option value="TS">Telangana (Hyderabad)</option><option value="">None</option>
              </select>
            </label>
          )}
        </div>
        <div className="flex flex-wrap gap-4 text-xs text-zinc-400">
          {country === "IN" && (
            <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={!opt.pfCapped} onChange={(e) => set("pfCapped", !e.target.checked)} />PF on full basic (not capped at ₹15,000)</label>
          )}
          {country !== "AE" && (
            <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={!!opt.includeTax} onChange={(e) => set("includeTax", e.target.checked)} />Take off estimated income tax</label>
          )}
        </div>
      </div>

      {r && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Annual CTC" value={m(r.ctc)} tone="good" />
            <Stat label="In-hand a month" value={m(r.inHand)} />
            <Stat label="Gross a month" value={m(r.gross)} sub={`Basic ${m(r.basic)}`} />
            <Stat label="Cost to LIFTR a month" value={m(r.monthlyCost)} sub="Gross + employer contributions" />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="card-surface p-4 text-sm">
              <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-2">Their pay a month</div>
              <Row l="Basic" v={m(r.basic)} /><Row l="Allowances" v={m(r.allowances)} /><Row l="Gross" v={m(r.gross)} strong />
              {r.employeeDeductions.map(([l, v]) => <Row key={l} l={`Less ${l}`} v={`−${m(v)}`} />)}
              <Row l="Estimated in-hand" v={m(r.inHand)} strong />
            </div>
            <div className="card-surface p-4 text-sm">
              <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-2">LIFTR pays on top</div>
              {r.employerContributions.map(([l, v]) => <Row key={l} l={l} v={`${m(v)} a month`} />)}
              {r.annualExtras.map(([l, v]) => <Row key={l} l={l} v={`${m(v)} a year`} />)}
              {!r.employerContributions.length && !r.annualExtras.length && <div className="text-zinc-500 text-xs">Nothing on top.</div>}
              <Row l="Annual CTC" v={m(r.ctc)} strong />
            </div>
          </div>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-[11px] text-zinc-500 max-w-2xl">
              An estimate for offers, not a payslip. Income tax assumes no declarations ({country === "IN" ? "new regime" : country === "LK" ? "APIT from April 2025" : "none in the UAE"}).
              {country === "IN" ? " ESI applies while gross is ₹21,000 or less. Gratuity is accrued, paid after 5 years." : ""}
            </p>
            <Button size="sm" variant="outline" onClick={copy}>Copy breakdown</Button>
          </div>
        </>
      )}
    </div>
  );
}

function Row({ l, v, strong }) {
  return (
    <div className={`flex justify-between py-1 border-b border-[#1a1a1a] last:border-0 ${strong ? "text-zinc-100 font-medium" : "text-zinc-400"}`}>
      <span>{l}</span><span className="tabular-nums">{v}</span>
    </div>
  );
}
